import React, { useEffect, useState } from 'react';
import { Package, ShoppingCart, Users, DollarSign, Clock, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';

const STATUS_COLORS: Record<string, string> = {
  pending: '#F59E0B',
  confirmed: '#3B82F6',
  processing: '#8B5CF6',
  shipped: '#6366F1',
  delivered: '#10B981',
  cancelled: '#EF4444',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

function pct(a: number, b: number) {
  if (!b) return 0;
  return Math.round(((a - b) / b) * 100);
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-4 py-3 text-sm">
        <p className="text-gray-500 font-medium mb-1">{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.color }} className="font-semibold">
            {p.name}: {String(p.name).toLowerCase().includes('revenue') ? `Rs.${Number(p.value).toLocaleString()}` : p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};
export default function AdminDashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard/stats')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {Array(4).fill(null).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl h-32 animate-pulse border border-gray-100" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array(4).fill(null).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl h-72 animate-pulse border border-gray-100" />
        ))}
      </div>
    </div>
  );

  const stats = data?.stats || {};
  const monthlyStats: any[] = data?.monthly_stats || [];
  const dailyOrders: any[] = data?.daily_orders || [];
  const monthlyUsers: any[] = data?.monthly_users || [];
  const categorySales: any[] = data?.category_sales || [];
  const ordersByStatus: any[] = data?.orders_by_status || [];

  const statCards = [
    { label: 'Total Revenue', value: `Rs.${Number(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'bg-emerald-500', thisMonth: stats.this_month_revenue || 0, lastMonth: stats.last_month_revenue || 0 },
    { label: 'Total Orders', value: stats.total_orders || 0, icon: ShoppingCart, color: 'bg-blue-500', thisMonth: stats.this_month_orders || 0, lastMonth: stats.last_month_orders || 0 },
    { label: 'Total Products', value: stats.total_products || 0, icon: Package, color: 'bg-violet-500', thisMonth: stats.total_products || 0, lastMonth: stats.total_products || 0 },
    { label: 'Customers', value: stats.total_customers || 0, icon: Users, color: 'bg-orange-500', thisMonth: stats.this_month_customers || 0, lastMonth: stats.last_month_customers || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Welcome back! Here's what's happening.</p>
        </div>
        <p className="text-sm text-gray-400">{new Date().toLocaleDateString('en-PK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card, i) => {
          const change = pct(card.thisMonth, card.lastMonth);
          const isUp = change >= 0;
          return (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider truncate">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1.5 truncate">{card.value}</p>
                  <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isUp ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                    <span>{Math.abs(change)}% vs last month</span>
                  </div>
                </div>
                <div className={`${card.color} p-3 rounded-xl flex-shrink-0 ml-3`}>
                  <card.icon size={22} className="text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending Alert */}
      {stats.pending_orders > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Clock size={20} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-amber-900 text-sm">{stats.pending_orders} pending order{stats.pending_orders > 1 ? 's' : ''} need attention</p>
            <p className="text-xs text-amber-700 mt-0.5">Please review and confirm new orders promptly</p>
          </div>
          <Link to="/admin/orders" className="flex-shrink-0 bg-amber-600 text-white px-4 py-2 text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors">Review</Link>
        </div>
      )}

      {/* Charts Row 1: Revenue Area + Orders Pie */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-semibold text-gray-900">Revenue &amp; Orders</h2>
              <p className="text-xs text-gray-500 mt-0.5">Last 12 months overview</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-900 inline-block" />Revenue</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-400 inline-block" />Orders</span>
            </div>
          </div>
          {monthlyStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthlyStats} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#111827" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#111827" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="short" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#111827" strokeWidth={2} fill="url(#gRevenue)" />
                <Area yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#60A5FA" strokeWidth={2} fill="url(#gOrders)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-56 flex items-center justify-center text-gray-300 text-sm">No data yet — orders will appear here</div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-4">
            <h2 className="font-semibold text-gray-900">Orders by Status</h2>
            <p className="text-xs text-gray-500 mt-0.5">Distribution breakdown</p>
          </div>
          {ordersByStatus.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={ordersByStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3}>
                    {ordersByStatus.map((entry: any, i: number) => (
                      <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#9CA3AF'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any, name: any) => [v, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {ordersByStatus.map((s: any) => {
                  const p = stats.total_orders ? Math.round((s.count / stats.total_orders) * 100) : 0;
                  return (
                    <div key={s.status} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-2 capitalize text-gray-600 font-medium">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[s.status] || '#9CA3AF' }} />
                        {s.status}
                      </span>
                      <span className="text-gray-400">{s.count} ({p}%)</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-56 flex items-center justify-center text-gray-300 text-sm">No orders yet</div>
          )}
        </div>
      </div>

      {/* Charts Row 2: Daily Orders + New Customers */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900">Daily Orders (Last 14 Days)</h2>
            <p className="text-xs text-gray-500 mt-0.5">Orders received per day</p>
          </div>
          {dailyOrders.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dailyOrders} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="orders" name="Orders" fill="#111827" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No recent orders</div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900">New Customers</h2>
            <p className="text-xs text-gray-500 mt-0.5">Monthly registrations</p>
          </div>
          {monthlyUsers.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyUsers} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="users" name="Users" fill="#F97316" radius={[4, 4, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No user data yet</div>
          )}
        </div>
      </div>

      {/* Category Sales */}
      {categorySales.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900">Sales by Category</h2>
            <p className="text-xs text-gray-500 mt-0.5">Revenue breakdown per category</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={categorySales} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="sales" name="Revenue" fill="#8B5CF6" radius={[0, 4, 4, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bottom: Recent Orders + Top Products */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <h2 className="font-semibold text-gray-900">Recent Orders</h2>
            <Link to="/admin/orders" className="text-xs text-blue-600 hover:text-blue-800 font-medium">View All →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-50/50">
                  <th className="px-6 py-3">Order</th>
                  <th className="px-6 py-3">Customer</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data?.recent_orders || []).slice(0, 8).map((order: any) => (
                  <tr key={order.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-6 py-3">
                      <Link to="/admin/orders" className="text-sm font-mono font-medium text-blue-600 hover:text-blue-800">{order.order_number}</Link>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">{order.first_name} {order.last_name}</td>
                    <td className="px-6 py-3 text-sm font-semibold text-gray-900">Rs.{Number(order.total).toLocaleString()}</td>
                    <td className="px-6 py-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${statusColors[order.status] || 'bg-gray-100 text-gray-600'}`}>{order.status}</span>
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString('en-PK')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data?.recent_orders?.length && (
              <div className="py-12 text-center text-gray-300 text-sm">No orders yet</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
            <h2 className="font-semibold text-gray-900">Top Products</h2>
            <Link to="/admin/products" className="text-xs text-blue-600 hover:text-blue-800 font-medium">View All →</Link>
          </div>
          <div className="divide-y divide-gray-50">
            {(data?.top_products || []).slice(0, 6).map((product: any, i: number) => (
              <div key={product.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/60 transition-colors">
                <span className="text-xs font-bold text-gray-300 w-4 flex-shrink-0">#{i + 1}</span>
                <div className="w-10 h-12 bg-gray-100 flex-shrink-0 rounded overflow-hidden">
                  {product.featured_image && <img src={getImageUrl(product.featured_image)} alt={product.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                  <p className="text-xs text-gray-400">Rs.{Number(product.price).toLocaleString()}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-gray-900">{product.order_items_count}</p>
                  <p className="text-xs text-gray-400">sold</p>
                </div>
              </div>
            ))}
            {!data?.top_products?.length && (
              <div className="py-12 text-center text-gray-300 text-sm">No products yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
